import { useState, useMemo, useEffect, type ReactNode } from 'react'
import { ViolationDetail, ViolationMeta, ViolationStatusGlyph } from './ViolationRow'
import ScoreDeltaChip from '@/components/Dashboard/v2/ScoreDeltaChip'
import { ContainedTabs } from '@/components/carbon/ContainedTabs'
import { DataTable, type DataColumn, type DataTableSort } from '@/components/carbon/DataTable'
import { TableFilterMenu } from '@/components/carbon/TableFilterMenu'
import { TableSearch } from '@/components/carbon/TableSearch'
import { TableSelect } from '@/components/carbon/TableSelect'
import { Tag } from '@/components/carbon/Tag'
import { FRAMEWORK_ORDER, FRAMEWORK_PATTERNS, refMatchesFramework } from '@/lib/frameworkUtils'
import { formatShortDate } from '@/lib/compliance-format'
import type { ComplianceViolation, PolicyCriticality, ViolationStatus } from '@/types'

interface ViolationsTableProps {
  violations: ComplianceViolation[]
  isLoading?: boolean
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
  /** Default criticality filter. Defaults to 'all' */
  defaultCriticalityFilter?: PolicyCriticality | 'all'
  /** Default status filter. Defaults to 'all' */
  defaultStatusFilter?: ViolationStatus | 'all'
  /** Framework code for contextual framework reference display (e.g., "EIDSCA", "SCUBA") */
  frameworkCode?: string
  /** Policy code to focus: scrolls into view, expands the row, and clears filters that would hide it. */
  focusPolicyCode?: string | null
  /** Called once the focus has been consumed (scroll initiated). */
  onFocusConsumed?: () => void
  /** The table's title and accessible name. Default per view: "Failing controls", "Passing controls", "Every control". */
  title?: string
  /** One line under the title (what was checked). */
  description?: ReactNode
  /** Toolbar controls placed after the search (a Workload select, say). */
  toolbarStart?: ReactNode
}

type ViewMode = 'failing' | 'passing' | 'all'

/** Short labels for the framework tags and the Framework select ("SCuBA", "NIST 800-53"). */
const FRAMEWORK_SHORT: Record<string, string> = {
  SCUBA: 'SCuBA',
  NIST_800_53: 'NIST 800-53',
  NIST_CSF: 'NIST CSF',
  MCSB: 'MCSB',
  EIDSCA: 'EIDSCA',
  MAESTER: 'Maester',
  CIS: 'CIS',
}

/** Distinct framework codes a control maps to, in reference order. */
function frameworkCodesOf(violation: Pick<ComplianceViolation, 'frameworkReferences'>): string[] {
  const out: string[] = []
  for (const ref of violation.frameworkReferences ?? []) {
    const code = Object.keys(FRAMEWORK_PATTERNS).find(c => refMatchesFramework(ref, c))
    if (code && !out.includes(code)) out.push(code)
  }
  return out
}
type SeverityTab = PolicyCriticality | 'all'

const isPassing = (v: ComplianceViolation) => v.status === 'Pass'

const VIEW_TITLE: Record<ViewMode, string> = {
  failing: 'Failing controls',
  passing: 'Passing controls',
  all: 'Every control',
}

const VIEW_NOUN: Record<ViewMode, string> = {
  failing: 'failing controls',
  passing: 'passing controls',
  all: 'controls',
}

/** Rows per page. The list is client-side (the tab loads every control), so it pages in place. */
const PAGE_SIZE = 25

const CRITICALITY_RANK: Record<PolicyCriticality, number> = { SHALL: 3, SHOULD: 2, MAY: 1 }
// Lifecycle states sort under every open tier: a passing row never outranks a failing one.
const STATUS_RANK: Record<string, number> = { Open: 3, DataUnavailable: 2, Waived: 1, Resolved: 0, Pass: 0 }

const STATUS_OPTIONS: { value: ViolationStatus; label: string }[] = [
  { value: 'Open', label: 'Open' },
  { value: 'Waived', label: 'Waived' },
  { value: 'Resolved', label: 'Resolved' },
  { value: 'DataUnavailable', label: 'No data' },
]

/** Epoch ms of a row's first detection; an unparseable date sorts last. */
const firstSeen = (v: ComplianceViolation) => {
  const iso = v.firstDetectedAt ?? v.detectedAt
  const t = iso ? new Date(iso).getTime() : NaN
  return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t
}

/** Severity, open rows first: an open critical leads, a passing control trails. */
function severityRank(v: ComplianceViolation): number {
  return (STATUS_RANK[v.status] ?? 0) * 10 + (CRITICALITY_RANK[v.criticality] ?? 0)
}

/**
 * Compliance controls as a Carbon DataTable (DESIGN.md "Data table", mockup
 * 03 "Failing controls"):
 *   head     the title (also the table's name) over a one-line description
 *   tabs     contained tabs by severity: All, Critical, High, Medium (three
 *            tiers, §7 C14/X6) with count tags
 *   toolbar  search, the caller's selects, Framework, Show (failing, passing
 *            or every control) and a filter menu for status and category
 *   rows     severity (glyph and word), the control (title, GS lift, workload
 *            and rule code), its frameworks, and when it started failing; a row
 *            expands in place into What Guardian found / How to fix it /
 *            Frameworks with a full-width button set
 *   footer   the shown count and the sort order; past 25 rows, the sort
 *            order over a pagination bar (25 a page)
 * Data stays here: the table filters, sorts and pages the rows it is given. A
 * focus request (`focusPolicyCode`) clears the filters, opens the page that
 * holds that row, expands it and scrolls to it.
 */
export default function ViolationsTable({
  violations,
  isLoading = false,
  onAddWaiver,
  onEditWaiver,
  onDeleteWaiver,
  defaultCriticalityFilter = 'all',
  defaultStatusFilter = 'all',
  frameworkCode,
  focusPolicyCode,
  onFocusConsumed,
  title,
  description,
  toolbarStart,
}: ViolationsTableProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('failing')
  const [searchQuery, setSearchQuery] = useState('')
  const [severityTab, setSeverityTab] = useState<SeverityTab>(defaultCriticalityFilter)
  const [statusFilter, setStatusFilter] = useState<string[]>(defaultStatusFilter === 'all' ? [] : [defaultStatusFilter])
  const [categoryFilter, setCategoryFilter] = useState<string[]>([])
  const [frameworkFilter, setFrameworkFilter] = useState<string>('all')
  const [sort, setSort] = useState<DataTableSort | null>(null)
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [page, setPage] = useState(1)

  // A new row set from the caller (its Workload select) starts on the first
  // page, as every filter here does (React's adjust-state-on-prop-change pattern).
  const [pagedRowsFor, setPagedRowsFor] = useState(violations)
  if (pagedRowsFor !== violations) {
    setPagedRowsFor(violations)
    setPage(1)
  }
  /** Wrap a filter setter so a change also returns to the first page. */
  const resetting =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value)
      setPage(1)
    }

  // When a focus is requested, drop view-mode and column filters so the target row
  // is guaranteed to be visible regardless of the user's prior filter state, and
  // open its detail.
  useEffect(() => {
    if (!focusPolicyCode) return
    setViewMode('all')
    setSearchQuery('')
    setSeverityTab('all')
    setStatusFilter([])
    setCategoryFilter([])
    setFrameworkFilter('all')
    const target = violations.find(v => v.policyCode === focusPolicyCode)
    if (target) setExpandedIds(prev => (prev.includes(target.id) ? prev : [...prev, target.id]))
  }, [focusPolicyCode, violations])

  // Unique categories for the filter menu
  const categories = useMemo(() => {
    const uniqueCategories = new Set(violations.map((v) => v.category).filter(Boolean))
    return Array.from(uniqueCategories).sort()
  }, [violations])

  // The frameworks the rows map to, for the Framework select. A framework-scoped
  // table (frameworkCode) has no use for it.
  const frameworkOptions = useMemo(() => {
    if (frameworkCode) return []
    const codes = new Set<string>()
    for (const v of violations) for (const c of frameworkCodesOf(v)) codes.add(c)
    return Array.from(codes).sort((a, b) => (FRAMEWORK_ORDER[a] ?? 99) - (FRAMEWORK_ORDER[b] ?? 99) || a.localeCompare(b))
  }, [violations, frameworkCode])

  // Apply every filter EXCEPT viewMode and the severity tab; used for the per-mode counts.
  const withoutModeFilter = useMemo(() => {
    let filtered = violations

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (v) =>
          v.policyCode.toLowerCase().includes(query) ||
          v.policyTitle.toLowerCase().includes(query) ||
          v.message.toLowerCase().includes(query) ||
          v.category.toLowerCase().includes(query) ||
          v.product.toLowerCase().includes(query)
      )
    }

    // Status is a lifecycle filter (Open/Waived/Resolved/No data); Passing view clears it.
    if (statusFilter.length > 0) {
      filtered = filtered.filter((v) => statusFilter.includes(v.status))
    }

    if (categoryFilter.length > 0) {
      filtered = filtered.filter((v) => categoryFilter.includes(v.category))
    }

    if (frameworkFilter !== 'all') {
      filtered = filtered.filter((v) => (v.frameworkReferences ?? []).some(ref => refMatchesFramework(ref, frameworkFilter)))
    }

    return filtered
  }, [violations, searchQuery, statusFilter, categoryFilter, frameworkFilter])

  // "Failing" means open. Waived, resolved and not-evaluated controls are not
  // failing, so the failing view holds them only when the Status filter names
  // them (the heatmap's Waived handoff, or the user's pick). Every count below
  // uses this one rule, so "All 61" heads 61 rows.
  const isFailing = useMemo(
    () =>
      statusFilter.length > 0
        ? (v: ComplianceViolation) => !isPassing(v) && statusFilter.includes(v.status)
        : (v: ComplianceViolation) => v.status === 'Open',
    [statusFilter],
  )

  // The Show select's counts reflect what the user would see in each mode, honouring other filters.
  const modeCounts = useMemo(() => {
    let failing = 0
    let passing = 0
    for (const v of withoutModeFilter) {
      if (isPassing(v)) passing++
      else if (isFailing(v)) failing++
    }
    return { failing, passing, all: withoutModeFilter.length }
  }, [withoutModeFilter, isFailing])

  const inMode = useMemo(() => {
    if (viewMode === 'failing') return withoutModeFilter.filter(isFailing)
    if (viewMode === 'passing') return withoutModeFilter.filter(isPassing)
    return withoutModeFilter
  }, [withoutModeFilter, viewMode, isFailing])

  // Severity tab counts follow the active view: failing rows (as above) for
  // failing, passing rows for passing, and the whole set for every control.
  const severityCounts = useMemo(() => {
    const predicate: (v: ComplianceViolation) => boolean =
      viewMode === 'passing' ? isPassing : viewMode === 'failing' ? isFailing : () => true
    const count = (c: PolicyCriticality) => violations.filter((v) => v.criticality === c && predicate(v)).length
    return { all: violations.filter(predicate).length, SHALL: count('SHALL'), SHOULD: count('SHOULD'), MAY: count('MAY') }
  }, [violations, viewMode, isFailing])

  // Filter by the severity tab, then sort. With no column sort the order is
  // severity, then how long each has failed (oldest first), as the footer says.
  const rows = useMemo(() => {
    const filtered = severityTab === 'all' ? inMode : inMode.filter((v) => v.criticality === severityTab)
    const dir = sort?.direction === 'desc' ? -1 : 1
    return [...filtered].sort((a, b) => {
      switch (sort?.columnId) {
        case 'severity':
          return dir * (severityRank(a) - severityRank(b)) || firstSeen(a) - firstSeen(b)
        case 'control':
          return dir * a.policyTitle.localeCompare(b.policyTitle)
        case 'since':
          return dir * (firstSeen(a) - firstSeen(b))
        default:
          return severityRank(b) - severityRank(a) || firstSeen(a) - firstSeen(b) || a.policyTitle.localeCompare(b.policyTitle)
      }
    })
  }, [inMode, severityTab, sort])

  // Paging. While a focus request is pending, the page is the one holding its
  // row (derived, so a deep link lands there on the first render); the scroll
  // below then keeps that page once the request is consumed.
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const focusIndex = focusPolicyCode ? rows.findIndex((v) => v.policyCode === focusPolicyCode) : -1
  const focusPage = focusIndex >= 0 ? Math.floor(focusIndex / PAGE_SIZE) + 1 : null
  const currentPage = focusPage ?? Math.min(page, pageCount)
  const paged = rows.length > PAGE_SIZE
  const pageRows = paged ? rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE) : rows

  useEffect(() => {
    // Don't scroll until the target row actually survives the filter pipeline.
    // The filter-reset effect above schedules setState; in the same render this
    // effect would otherwise RAF against the pre-reset DOM, getElementById
    // would return null, and onFocusConsumed would clear the focus before the
    // row ever rendered — that's the "click twice to scroll" symptom.
    if (!focusPolicyCode || focusPage == null) return

    // Double-RAF: the first frame lets the row's expansion commit (the row
    // height jumps from collapsed to expanded). The second frame scrolls
    // against the now-final layout so block:'center' lands on the row.
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = document.getElementById(`violation-${focusPolicyCode}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        setPage(focusPage)
        onFocusConsumed?.()
      })
    })
    return () => {
      cancelAnimationFrame(raf1)
      if (raf2) cancelAnimationFrame(raf2)
    }
  }, [focusPolicyCode, focusPage, rows, onFocusConsumed])

  const columns: DataColumn<ComplianceViolation>[] = [
    {
      id: 'severity',
      header: 'Severity',
      sortable: true,
      nowrap: true,
      width: '9rem',
      cell: (v) => <ViolationStatusGlyph violation={v} />,
    },
    {
      id: 'control',
      header: 'Control',
      sortable: true,
      primary: true,
      cell: (v) => (
        <span id={`violation-${v.policyCode}`} className="block min-w-0 scroll-mt-32">
          <b className="mr-2 font-semibold text-text-primary">{v.policyTitle}</b>
          <span className="inline-flex align-middle">
            <ScoreDeltaChip delta={v.guardianScoreDelta} omitAriaLabel />
          </span>
          <ViolationMeta violation={v} frameworkCode={frameworkCode} compact />
        </span>
      ),
    },
    {
      id: 'frameworks',
      header: 'Frameworks',
      hideBelow: 'c-lg',
      cell: (v) => {
        const codes = frameworkCodesOf(v)
        return codes.length ? (
          <span className="flex flex-wrap gap-1">
            {codes.map((c) => (
              <Tag key={c} kind="outline" size="sm">
                {FRAMEWORK_SHORT[c] ?? c}
              </Tag>
            ))}
          </span>
        ) : (
          <span className="text-text-helper">None mapped</span>
        )
      },
    },
    {
      id: 'since',
      header: viewMode === 'failing' ? 'Failing since' : 'First detected',
      sortable: true,
      nowrap: true,
      cell: (v) => <span className="tabular-nums">{formatShortDate(v.firstDetectedAt ?? v.detectedAt) || 'Not recorded'}</span>,
    },
  ]

  const sortNote = !sort
    ? viewMode === 'passing'
      ? 'Sorted by severity, then by first detection'
      : 'Sorted by severity, then by how long each has failed'
    : `Sorted by ${sort.columnId === 'since' ? (viewMode === 'failing' ? 'failing since' : 'first detected') : sort.columnId}, ${
        sort.direction === 'asc' ? 'ascending' : 'descending'
      }`

  const emptyCopy =
    violations.length === 0
      ? 'Guardian has no results for these controls yet.'
      : viewMode === 'passing'
        ? 'No passing controls match these filters.'
        : 'Nothing to review in this view.'

  const filterGroups = [
    ...(viewMode !== 'passing'
      ? [{ legend: 'Status', options: STATUS_OPTIONS, selected: statusFilter, onChange: resetting(setStatusFilter) }]
      : []),
    ...(categories.length > 1
      ? [{ legend: 'Category', options: categories.map((c) => ({ value: c, label: c })), selected: categoryFilter, onChange: resetting(setCategoryFilter) }]
      : []),
  ]

  return (
    <DataTable<ComplianceViolation>
      title={title ?? VIEW_TITLE[viewMode]}
      description={description}
      columns={columns}
      rows={pageRows}
      rowId={(v) => v.id}
      rowLabel={(v) => v.policyTitle}
      isLoading={isLoading}
      sort={sort}
      onSortChange={resetting(setSort)}
      empty={emptyCopy}
      tabs={
        <ContainedTabs
          aria-label="Severity"
          value={severityTab}
          onChange={(id) => resetting(setSeverityTab)(id as SeverityTab)}
          tabs={[
            { id: 'all', label: 'All', count: severityCounts.all },
            { id: 'SHALL', label: 'Critical', count: severityCounts.SHALL },
            { id: 'SHOULD', label: 'High', count: severityCounts.SHOULD },
            { id: 'MAY', label: 'Medium', count: severityCounts.MAY },
          ]}
        />
      }
      toolbar={
        <>
          <TableSearch value={searchQuery} onChange={resetting(setSearchQuery)} label="Search controls" />
          {toolbarStart}
          {frameworkOptions.length > 1 && (
            <TableSelect
              label="Framework"
              value={frameworkFilter}
              onChange={resetting(setFrameworkFilter)}
              options={[{ value: 'all', label: 'All' }, ...frameworkOptions.map((c) => ({ value: c, label: FRAMEWORK_SHORT[c] ?? c }))]}
            />
          )}
          <TableSelect
            label="Show"
            value={viewMode}
            onChange={(mode) => {
              setViewMode(mode as ViewMode)
              setPage(1)
              // Status applies to lifecycle states (Open/Waived/Resolved) that Pass rows
              // don't have, so reset it and Passing never silently hides data.
              if (mode === 'passing') setStatusFilter([])
            }}
            options={[
              { value: 'failing', label: `Failing (${modeCounts.failing})` },
              { value: 'passing', label: `Passing (${modeCounts.passing})` },
              { value: 'all', label: `All (${modeCounts.all})` },
            ]}
          />
          {filterGroups.length > 0 && (
            <TableFilterMenu
              label="Filter controls"
              groups={filterGroups}
              onReset={() => {
                setStatusFilter([])
                setCategoryFilter([])
                setPage(1)
              }}
            />
          )}
        </>
      }
      expandable={{
        render: (v) => (
          <ViolationDetail
            violation={v}
            onAddWaiver={onAddWaiver}
            onEditWaiver={onEditWaiver}
            onDeleteWaiver={onDeleteWaiver}
          />
        ),
        expandedIds,
        onExpandedChange: setExpandedIds,
      }}
      footer={
        <>
          {/* Paged, the bar below gives the range ("1–25 of 87"); the count here would claim every row is on screen. */}
          {!paged && (
            <span className="tabular-nums">
              {rows.length} of {modeCounts[viewMode]} {VIEW_NOUN[viewMode]} shown
            </span>
          )}
          <span className="text-helper text-text-helper">{sortNote}</span>
        </>
      }
      pagination={
        paged
          ? { page: currentPage, pageSize: PAGE_SIZE, total: rows.length, onPageChange: setPage, itemLabel: VIEW_NOUN[viewMode] }
          : undefined
      }
    />
  )
}
